import http from '../../client';
import { errorHandler } from '../../errorHandler';
import type { ApiResponse } from '../../types';
import type { Recipe } from '../../admin/RecipeApi';
import { normalizeRecipeId } from '../../../utils/recipeId';
import type { EngagementRequestOptions } from '../engagementTypes';

function profileParams(
  options?: EngagementRequestOptions,
): Record<string, string> | undefined {
  if (!options?.profileId?.trim()) return undefined;
  return { profileId: options.profileId.trim() };
}

export const likeCollectionApi = {
  /**
   * Toggle like status for a remedy (supports both UUID and numeric ID)
   * POST /recipes/:recipeId/toggle-like
   */
  toggleLike: async (
    recipeId: number | string,
    options?: EngagementRequestOptions,
  ): Promise<ApiResponse<{ liked: boolean; message?: string }>> => {
    try {
      const id = normalizeRecipeId(recipeId);
      const body: Record<string, unknown> = {};
      const p = profileParams(options);
      if (p) Object.assign(body, p);
      const response = await http.post(`/recipes/${id}/toggle-like`, body);
      return {
        success: true,
        data: response.data?.data || response.data || { liked: true },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  /**
   * Get all liked remedies for the acting profile
   * GET /recipes/liked
   */
  getLikedRecipes: async (
    options?: EngagementRequestOptions,
  ): Promise<ApiResponse<Recipe[]>> => {
    try {
      const response = await http.get('/recipes/liked', {
        params: profileParams(options),
      });

      const recipes =
        response.data?.likedRecipes ||
        response.data?.data?.likedRecipes ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      return {
        success: true,
        data: Array.isArray(recipes) ? recipes : [],
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
        data: [],
      };
    }
  },

  /**
   * Get collection names
   * GET /collections/names
   */
  getCollectionNames: async (
    options?: EngagementRequestOptions,
  ): Promise<ApiResponse<Array<{ id: string | number; name: string }>>> => {
    try {
      const response = await http.get('/collections/names', {
        params: profileParams(options),
      });

      const collections =
        response.data?.collections ||
        response.data?.data?.collections ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      return {
        success: true,
        data: Array.isArray(collections) ? collections : [],
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
        data: [],
      };
    }
  },

  /**
   * Get all collections
   * GET /collections
   */
  getCollections: async (
    options?: EngagementRequestOptions,
  ): Promise<
    ApiResponse<
      Array<{ id: string | number; name: string; recipeIds?: string[] }>
    >
  > => {
    try {
      const response = await http.get('/collections', {
        params: profileParams(options),
      });

      let collections =
        response.data?.collections ||
        response.data?.data?.collections ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      if (!Array.isArray(collections)) {
        collections = [];
      }

      return {
        success: true,
        data: collections,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
        data: [],
      };
    }
  },

  /**
   * Get recipes in a collection
   * GET /collections/:collectionId/recipes
   */
  getCollectionRecipes: async (
    collectionId: number | string,
    options?: EngagementRequestOptions,
  ): Promise<ApiResponse<Recipe[]>> => {
    try {
      const response = await http.get(`/collections/${collectionId}/recipes`, {
        params: profileParams(options),
      });

      const recipes =
        response.data?.collection?.recipes ||
        response.data?.recipes ||
        response.data?.data?.recipes ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      return {
        success: true,
        data: Array.isArray(recipes) ? recipes : [],
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
        data: [],
      };
    }
  },

  /**
   * Delete a collection
   * DELETE /collections/:collectionId
   */
  deleteCollection: async (
    collectionId: number | string,
    options?: EngagementRequestOptions,
  ): Promise<ApiResponse<{ message?: string }>> => {
    try {
      await http.delete(`/collections/${collectionId}`, {
        params: profileParams(options),
      });
      return {
        success: true,
        data: { message: 'Collection deleted' },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  /**
   * Update collection name
   * PATCH /collections/:collectionId
   */
  updateCollectionName: async (
    collectionId: number | string,
    newName: string,
    options?: EngagementRequestOptions,
  ): Promise<
    ApiResponse<{ id: string | number; name: string; created_at?: string }>
  > => {
    try {
      const body: Record<string, unknown> = { name: newName.trim() };
      const p = profileParams(options);
      if (p) Object.assign(body, p);
      const response = await http.patch(`/collections/${collectionId}`, body);
      const collection =
        response.data?.collection || response.data?.data || response.data;
      return {
        success: true,
        data: collection,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  /**
   * Toggle collection for a remedy (create/add to collection)
   * POST /recipes/:recipeId/toggle-collection
   */
  toggleCollection: async (
    recipeId: number | string,
    collectionName: string,
    options?: EngagementRequestOptions,
  ): Promise<
    ApiResponse<{ collectionId?: string | number; message?: string }>
  > => {
    try {
      const id = normalizeRecipeId(recipeId);
      const body: Record<string, unknown> = { collectionName };
      const p = profileParams(options);
      if (p) Object.assign(body, p);
      const response = await http.post(
        `/recipes/${id}/toggle-collection`,
        body,
      );
      return {
        success: true,
        data: response.data?.data || response.data || {},
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
